import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { IDLE_LOGOUT_STORAGE_KEY } from "@/core/constants/keys";
import { Paths } from "@/core/constants/paths";
import { getIdleTimeoutMs } from "@/core/environment";
import { logWarning } from "@/core/services";
import { clearAuthToken, getAuthToken } from "./auth.service";
import { logoutFromBff } from "./auth.gateway";
import { createActivityStorage } from "./idle-activity-storage";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "click",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
] as const;

const WRITE_THROTTLE_MS = 10_000;
const CHECK_INTERVAL_MS = 30_000;

/** Aviso do login depois do logout por inatividade (o `LoginScreen` lê `state.logoutMessage`). */
export const IDLE_LOGOUT_MESSAGE = {
  code: "idle_timeout",
  severity: "info",
  summary: "Sessão encerrada",
  detail: "Você foi desconectado por inatividade.",
} as const;

/**
 * Desloga o usuário depois de `timeoutMs` sem atividade (padrão: `HUB_IDLE_TIMEOUT_MINUTES`).
 * `null` (recurso desligado) não registra nada. Deve rodar só em rota autenticada.
 */
export function useIdleLogout(timeoutMs: number | null = getIdleTimeoutMs()): void {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (timeoutMs === null) return;

    const storage = createActivityStorage();
    let finished = false;
    let lastWrite = Date.now();
    storage.write(lastWrite);

    const redirectToLogin = () => {
      navigateRef.current(Paths.LOGIN, {
        replace: true,
        state: { logoutMessage: IDLE_LOGOUT_MESSAGE },
      });
    };

    const stop = () => {
      finished = true;
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastWrite < WRITE_THROTTLE_MS) return;
      lastWrite = now;
      storage.write(now);
    };

    const logoutByInactivity = async () => {
      stop();
      try {
        await logoutFromBff();
      } catch (error) {
        logWarning(
          "Falha ao encerrar a sessão no BFF após inatividade",
          { error: error instanceof Error ? error.message : String(error) },
          { component: "useIdleLogout" },
        );
      }

      // `logoutFromBff` já limpa no `finally`; garante o token fora mesmo se algo o recriou.
      if (getAuthToken()) clearAuthToken();

      try {
        localStorage.setItem(IDLE_LOGOUT_STORAGE_KEY, String(Date.now()));
      } catch {
        // Sem storage, as outras abas caem pelo 401 / checagem delas.
      }
      redirectToLogin();
    };

    const checkIdle = () => {
      if (finished) return;
      const lastActivity = storage.read() ?? lastWrite;
      if (Date.now() - lastActivity > timeoutMs) void logoutByInactivity();
    };

    const handleStorage = (event: StorageEvent) => {
      if (finished || event.key !== IDLE_LOGOUT_STORAGE_KEY || !event.newValue) return;
      // Outra aba já encerrou a sessão no bff: só limpa o que for local e sai.
      stop();
      if (getAuthToken()) clearAuthToken();
      redirectToLogin();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkIdle();
    };

    const intervalId = window.setInterval(checkIdle, CHECK_INTERVAL_MS);
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, handleActivity, { passive: true, capture: true }),
    );
    window.addEventListener("focus", checkIdle);
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stop();
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, handleActivity, { capture: true }),
      );
      window.removeEventListener("focus", checkIdle);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [timeoutMs]);
}
